// ============================================================
// AI Rådgiver — morgenbrief, nattarbeid og hendelsesstrøm (lokal fallback)
// ============================================================
// Speil av functions/aiAdvisor/aiAdvisorBriefing.ts. Endres en regel der, endres den
// her. Alt er utledet av bokførte tall — ingen tall er funnet på.
const PlummeAiAdvisorBriefing = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const inYear = (value, year) => String(value || '').slice(0, 4) === String(year);

  const countAnalyzedTransactions = (state, year) => {
    const invoices = safeArray(state.invoices).filter((entry) => inYear(entry.date || entry.issueDate, year)).length;
    const expenses = safeArray(state.expenses).filter((entry) => inYear(entry.date, year)).length;
    const otherSales = safeArray(state.otherSales).filter((entry) => inYear(entry.date, year)).length;
    const mileage = safeArray(state.mileageEntries).filter((entry) => inYear(entry.date, year)).length;
    return invoices + expenses + otherSales + mileage;
  };

  const countCheckedVouchers = (state, year) =>
    safeArray(state.expenses).filter((expense) =>
      inYear(expense.date, year) &&
      Boolean(expense.attachmentId || expense.fileName || expense.receiptFile || safeArray(expense.attachments).length)).length;

  const buildHeroEffects = ({ home, settings }) => {
    const effects = [];
    const salesOverdue = num(home?.overview?.salesOverdue);
    if (salesOverdue > 0) effects.push({ type: 'liquidity', valueKind: 'amount', value: Math.round(salesOverdue), noteKey: 'earlierIn' });
    const purchasesOverdue = num(home?.overview?.purchasesOverdue);
    if (purchasesOverdue > 0) effects.push({ type: 'risk', valueKind: 'amount', value: Math.round(purchasesOverdue), noteKey: 'overduePayables' });
    const vatDue = num(home?.publicFees?.vatDue);
    if (settings?.vatRegistered && vatDue > 0) effects.push({ type: 'cost', valueKind: 'amount', value: Math.round(vatDue), noteKey: 'vatToCover' });
    const missingVouchers = num(home?.followup?.missingAttachmentCount);
    if (missingVouchers > 0) effects.push({ type: 'tax', valueKind: 'count', value: Math.round(missingVouchers), noteKey: 'vouchersAtRisk' });
    return effects.slice(0, 3);
  };

  const buildFeed = ({ home, costsList, incomeYoy, costsYoy, resultMargin, previousResultMargin, concentration }) => {
    const feed = [];
    if (resultMargin != null && previousResultMargin != null) {
      const delta = Math.round((resultMargin - previousResultMargin) * 10) / 10;
      if (Math.abs(delta) >= 1) {
        feed.push({
          key: delta < 0 ? 'marginDown' : 'marginUp',
          tone: delta < 0 ? 'red' : 'green',
          icon: delta < 0 ? 'trendDown' : 'trendUp',
          vars: { points: Math.abs(delta) },
          tab: 'salg',
        });
      }
    }
    const jumped = safeArray(costsList).filter((line) => line.flag).sort((a, b) => num(b.chg) - num(a.chg))[0];
    if (jumped) feed.push({ key: 'costLineJump', tone: 'amber', icon: 'receipt', vars: { name: jumped.name, pct: num(jumped.chg) }, tab: 'kostnader' });
    const salesOverdue = num(home?.overview?.salesOverdue);
    if (salesOverdue > 0) feed.push({ key: 'overdueSales', tone: 'amber', icon: 'clock', vars: { amount: Math.round(salesOverdue) }, route: 'fakturaliste' });
    const purchasesOverdue = num(home?.overview?.purchasesOverdue);
    if (purchasesOverdue > 0) feed.push({ key: 'overduePurchases', tone: 'amber', icon: 'receipt', vars: { amount: Math.round(purchasesOverdue) }, route: 'leverandorer' });
    if (concentration != null && concentration > 40) feed.push({ key: 'concentration', tone: 'amber', icon: 'users', vars: { pct: Math.round(concentration) }, tab: 'salg' });
    if (costsYoy != null && costsYoy < -5) feed.push({ key: 'costsDown', tone: 'green', icon: 'trendDown', vars: { pct: Math.abs(Math.round(costsYoy)) }, tab: 'kostnader' });
    if (incomeYoy != null && incomeYoy > 5) feed.push({ key: 'incomeUp', tone: 'green', icon: 'trendUp', vars: { pct: Math.round(incomeYoy) }, tab: 'salg' });
    if (num(home?.followup?.missingAttachmentCount) === 0) feed.push({ key: 'vouchersComplete', tone: 'green', icon: 'checkCircle', vars: {} });
    return feed.slice(0, 6);
  };

  // Modulen vises bare når bedriften faktisk har ansatte eller lønnskjøringer.
  const PAYROLL_ACCOUNT = /^5\d{3}$/;
  const buildEmployeesModule = ({ state, year, report, income }) => {
    const employees = safeArray(state.employees);
    const runs = safeArray(state.payrollRuns);
    if (!employees.length && !runs.length) return null;
    const payrollCost = safeArray(report?.sections)
      .find((section) => section.key === 'kostnader')?.lines
      ?.filter((line) => PAYROLL_ACCOUNT.test(String(line.nr)))
      .reduce((sum, line) => sum + Math.abs(num(line.amount)), 0) || 0;
    const runsThisYear = runs.filter((run) => inYear(run.date || run.paidAt || run.period, year)).length;
    return {
      employeeCount: employees.length,
      payrollCost: Math.round(payrollCost),
      payrollShare: income > 0 ? Math.round((payrollCost / income) * 1000) / 10 : null,
      runsThisYear,
    };
  };

  const buildAdvisorBriefing = ({
    state = {}, year, home, report, settings = {}, monitor = [], recommendations = [], costsList = [],
    income = 0, incomeYoy = null, costsYoy = null, resultMargin = null, previousResultMargin = null,
    concentration = null, generatedAt = null,
  } = {}) => ({
    overnight: {
      updatedAt: generatedAt || new Date().toISOString(),
      counts: {
        transactions: countAnalyzedTransactions(state, year),
        vouchers: countCheckedVouchers(state, year),
        risks: safeArray(monitor).length,
        recommendations: safeArray(recommendations).length,
      },
    },
    effects: buildHeroEffects({ home, settings }),
    feed: buildFeed({ home, costsList, incomeYoy, costsYoy, resultMargin, previousResultMargin, concentration }),
    employees: buildEmployeesModule({ state, year, report, income }),
  });

  return { buildAdvisorBriefing };
})();

Object.assign(globalThis, { PlummeAiAdvisorBriefing });
